'use es6';

import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'transmute/get';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import H2 from 'UIComponents/elements/headings/H2';
import UIIconHolder from 'UIComponents/icon/UIIconHolder';
import UIUnauthedHubSpotUserAvatar from 'ui-addon-avatars/UIUnauthedHubSpotUserAvatar';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIImage from 'UIComponents/image/UIImage';
import UIIllustration from 'UIComponents/image/UIIllustration';
import { getIsRoundRobin, getHeaderType, getHeaderOptions } from 'MeetingsLib/utils/utils';
import { getBookInfo as getBookInfoSelector, getOffline as getOfflineSelector } from 'MeetingsLib/selectors/BookInfoSelectors';
import { getIsFetchingPublicAvailability, getIsFetchingCompanyName, getMeetingsCancelStatus } from 'MeetingsLib/selectors/RequestStatusSelectors';
import { SUCCEEDED } from 'MeetingsBase/constants/RequestStatusTypes';
import UserProfile from 'MeetingsBase/models/UserProfile';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
class ProfileHeader extends Component {
  constructor(...args) {
    super(...args);
    this.getDefaultHeadline = () => {
      const {
        profile,
        companyName,
        linkType,
        offline,
        isFetchingCompanyName
      } = this.props;
      const isRoundRobin = getIsRoundRobin(linkType);
      if (isRoundRobin && isFetchingCompanyName) {
        return null;
      }
      const suffix = offline ? 'offline' : 'online';
      const headerType = getHeaderType(isRoundRobin);
      const options = getHeaderOptions(isRoundRobin, companyName, profile);
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: `prebook.${headerType}.${suffix}`,
        options: options
      });
    };
    this.getCancelHeadline = () => {
      const {
        cancelRequestStatus
      } = this.props;
      if (cancelRequestStatus === SUCCEEDED) {
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: "cancel.success.header"
        });
      }
      return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "cancel.confirm.header"
      });
    };
    this.renderImage = () => {
      const {
        avatar,
        booked,
        isFetchingPublicAvailability
      } = this.props;
      if (isFetchingPublicAvailability) {
        return this.renderSkeletonImage();
      }
      if (booked) {
        return this.renderBookedImage();
      }
      if (avatar) {
        return this.renderAvatarImage();
      }
      return this.renderUserImage();
    };
    this.renderBookedImage = () => {
      const {
        offline
      } = this.props;
      const name = offline ? 'successful-send' : 'success';
      return /*#__PURE__*/_jsx(UIIllustration, {
        "data-test-id": "booked-image",
        name: name,
        width: "40%",
        "aria-hidden": true
      });
    };
    this.renderSkeletonImage = () => {
      return /*#__PURE__*/_jsx("div", {
        "aria-hidden": true,
        className: "header-avatar",
        "data-test-id": "skeleton-image",
        children: /*#__PURE__*/_jsx(UIIconHolder, {
          name: "contacts",
          size: 32,
          className: "skeleton-image"
        })
      });
    };
    this.renderAvatarImage = () => {
      return /*#__PURE__*/_jsx(UIImage, {
        "data-test-id": "avatar-image",
        className: "header-avatar avatar-image user-provided-avatar",
        src: this.props.avatar,
        shape: "circle",
        size: "responsive",
        "aria-hidden": true
      });
    };
    this.renderUserImage = () => {
      const {
        profile
      } = this.props;
      const email = profile.email;
      if (!email) return this.renderSkeletonImage();
      return (
        /*#__PURE__*/
        // the avatar component doesn't support alt text, and doesn't provide useful additional info
        // so let's make this hidden to screen readers
        _jsx("div", {
          "aria-hidden": true,
          className: "header-avatar",
          children: /*#__PURE__*/_jsx(UIUnauthedHubSpotUserAvatar, {
            hubSpotUserEmail: email,
            className: "avatar-image",
            size: "lg",
            _isHasTooltip: false // experimental prop
            ,
            source: "meetings-public"
          })
        }, email)
      );
    };
    this.renderMessage = () => {
      const {
        headline,
        booked,
        cancelling,
        offline,
        isFetchingPublicAvailability,
        isRescheduling
      } = this.props;
      if (isFetchingPublicAvailability) {
        return null;
      }
      if (cancelling) {
        return this.getCancelHeadline();
      }
      if (booked) {
        const suffix = offline ? 'offline' : 'online';
        const rescheduleSuffix = offline ? 'offlineReschedule' : 'onlineReschedule';
        const desiredSuffix = isRescheduling ? rescheduleSuffix : suffix;
        return /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: `summary.header.${desiredSuffix}`
        });
      }
      if (headline) {
        return headline;
      }
      return this.getDefaultHeadline();
    };
    this.renderHeadline = () => {
      const classes = 'headline m-top-4 m-bottom-0 m-x-1 overflow-y-auto' + (this.props.booked ? " success-header" : "");
      return /*#__PURE__*/_jsx(H2, {
        "aria-level": 1,
        className: classes,
        style: {
          alignSelf: 'stretch'
        },
        children: this.renderMessage()
      });
    };
  }
  render() {
    return /*#__PURE__*/_jsxs(UIFlex, {
      direction: "column",
      align: "center",
      alignSelf: "center",
      className: "profile-header text-center",
      "data-test-id": "profile-header",
      children: [this.renderHeadline(), this.renderImage()]
    });
  }
}
ProfileHeader.propTypes = {
  // passed
  avatar: PropTypes.string,
  booked: PropTypes.bool.isRequired,
  cancelling: PropTypes.bool,
  headline: PropTypes.string,
  profile: PropTypes.instanceOf(UserProfile).isRequired,
  // loaded
  companyName: PropTypes.string,
  isFetchingCompanyName: PropTypes.bool,
  isFetchingPublicAvailability: PropTypes.bool,
  cancelRequestStatus: PropTypes.string,
  linkType: PropTypes.string,
  offline: PropTypes.bool,
  isRescheduling: PropTypes.bool
};
export default connect((state, ownProps) => ({
  // Rerenders component in response to translation
  activeLocale: state.activeLocale,
  companyName: state.companyName,
  isFetchingCompanyName: getIsFetchingCompanyName(state),
  isFetchingPublicAvailability: getIsFetchingPublicAvailability(state),
  cancelRequestStatus: getMeetingsCancelStatus(state),
  linkType: get('linkType', getBookInfoSelector(state)),
  offline: getOfflineSelector(state) || ownProps && ownProps.offline,
  isRescheduling: state.isRescheduling
}))(ProfileHeader);